import type { ReactNode } from 'react'
import { DateTime } from 'luxon'
import { getName, isNotEmpty, type AuditLog, type TinyUser, type UserId } from '@terros/common'
import { Skeleton } from '@mantine/core'
import { useQueryDisclosure } from '@hooks'
import { SizePrimitives, useTinyUser, useTranslation } from '@front/model'
import { MaterialSymbolIcon, TListItem, type TListItemProps } from '../terros'
import { UserAvatarSection } from '../entityInfo'
import { AuditDrawer } from './AuditDrawer'

const getRelativeTime = (time: number | undefined): string | undefined => {
  if (time) return DateTime.fromMillis(time).toRelative() ?? undefined
}

export const AuditRelativeInfo = ({
  by,
  at,
  type,
  modifiedBy,
}: {
  by?: UserId
  at?: number
  type: string
  modifiedBy?: AuditLog[]
}): ReactNode => {
  const { t } = useTranslation()
  const [opened, { open, close }] = useQueryDisclosure('audit-history', false)
  const timeString = getRelativeTime(at)
  const { data: user, isLoading } = useTinyUser(by)

  const sortedModifications = modifiedBy ? [...modifiedBy].sort((a, b) => b.timestamp - a.timestamp) : []
  const hasModifications = isNotEmpty(sortedModifications)

  const showChevron = type === 'Last Updated' && hasModifications

  if (showChevron) {
    return (
      <>
        <TListItem
          {...getListItemProps({ user, isLoading, at, type })}
          rightSection={<MaterialSymbolIcon icon='history_2' size={SizePrimitives[5]} />}
          tooltip={t('View history')}
          onClick={open}
        />
        <AuditDrawer close={close} modifiedBy={sortedModifications} opened={opened} />
      </>
    )
  }

  if (by) return <TListItem {...getListItemProps({ user, isLoading, at, type })} />
  if (at) return <TListItem detailBold details={timeString} label={type} />
  return null
}

const getListItemProps = ({
  user,
  isLoading,
  at,
  type,
}: {
  user?: TinyUser | undefined
  isLoading: boolean
  at: number | undefined
  type: string
}): TListItemProps => {
  const timeString = getRelativeTime(at)

  const label = user ? `${type} - ${timeString}` : type
  const details = user ? `by ${getName(user)}` : timeString

  return {
    detailBold: true,
    details,
    label,
    leftSection: user ? <UserAvatarSection isLoading={isLoading} size='sm' user={user} /> : undefined,
    children: isLoading ? <Skeleton h={SizePrimitives[8]} /> : undefined,
  }
}
